import { useEffect, useRef, useState } from 'react'
import { Spinner } from './Primitives'

interface Props {
  open: boolean
  title: string
  description: string
  /**
   * Si viene, hay que escribir exactamente este texto para poder confirmar.
   * Es lo que separa "quise reiniciar staging" de "reinicié producción".
   */
  confirmName?: string | null
  confirmLabel?: string
  danger?: boolean
  busy?: boolean
  onConfirm: (typedName: string | undefined) => void
  onCancel: () => void
}

export function ConfirmDialog({
  open, title, description, confirmName, confirmLabel = 'Confirmar',
  danger = true, busy = false, onConfirm, onCancel,
}: Props) {
  const [typed, setTyped] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) {
      setTyped('')
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onCancel])

  if (!open) return null
  const needsTyping = Boolean(confirmName)
  const canConfirm = !busy && (!needsTyping || typed === confirmName)

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" role="dialog" aria-modal>
      <div className="w-full max-w-md rounded-xl border bg-slate-900 p-6 shadow-xl">
        <h2 className="text-lg font-semibold">{title}</h2>
        <p className="mt-2 text-sm text-slate-400">{description}</p>

        {needsTyping && (
          <div className="mt-4">
            <label className="label">
              Escribí <code className="rounded bg-slate-800 px-1.5 py-0.5 font-mono text-amber-300">{confirmName}</code> para confirmar
            </label>
            <input
              ref={inputRef}
              className="input font-mono"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && canConfirm) onConfirm(typed)
              }}
              autoComplete="off"
              spellCheck={false}
            />
          </div>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button className="btn-ghost" onClick={onCancel} disabled={busy}>Cancelar</button>
          <button
            className={danger ? 'btn-danger' : 'btn-primary'}
            onClick={() => onConfirm(needsTyping ? typed : undefined)}
            disabled={!canConfirm}
          >
            {busy && <Spinner />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
